import * as THREE from '@phdsoft/three';
import { WorkableElement } from 'src/app/platform/busines-data/SectionElements/WorkableElement';
import { Rectd } from './repair-selection-state';

export interface OrthoFrameCandidate {
    element: WorkableElement;
    mesh: THREE.Object3D;
}

/**
 * Orthographic projection frame for repair-area insertion.
 *
 * Replaces the camera-apex selection frustum with a prism/cylinder extruded along the
 * hit element's normal `n`, so the resulting area on the surface is the true drawn shape
 * (rectangle/circle/ellipse) regardless of the camera angle — as if the camera were
 * positioned directly in front of the element.
 *
 * Built once per drag-end from the drawn screen outline:
 * - the outline is projected (through the camera) onto the surface plane of the element
 *   under the shape's center (the "footprint"),
 * - the footprint is rectified to an idealized shape: its center plus averaged extents,
 *   expressed in an in-plane orthonormal basis (u, v) with v = n × u.
 */
export class RepairOrthoFrame {
    public shapeType: 'rectangle' | 'circle' | 'ellipse';
    public n: THREE.Vector3;
    public origin: THREE.Vector3;
    public u: THREE.Vector3;
    public v: THREE.Vector3;
    public halfWidth = 0;
    public halfHeight = 0;
    public radius = 0;
    public radiusU = 0;
    public radiusV = 0;
    public hitElement: WorkableElement;

    // The true element surface at the hit, captured BEFORE the canonical-axis snap may
    // rotate `n`. The inserted geometry lives on this plane (prism ∩ surface), so the
    // preview must be projected onto it — not onto the n-perpendicular plane — to match.
    public hitPoint: THREE.Vector3;
    public surfaceNormal: THREE.Vector3;

    private rectCorners: THREE.Vector3[] = [];

    // when true, n is snapped to a canonical world axis and u/v are the two
    // remaining canonical axes (world-aligned areas)
    private canonicalBasis = false;

    // rays nearly parallel to the surface produce unusable footprints
    private static readonly GRAZING_LIMIT = 0.05;
    private static readonly MIN_EXTENT = 1e-6;
    private static readonly INSIDE_TOLERANCE = 1e-4;
    // max angle between the snapped canonical axis and the element's normal for the
    // snap to be accepted (beyond it, an oblique projection would distort the area)
    private static readonly SNAP_TOLERANCE_COS = Math.cos((25 * Math.PI) / 180);

    public static fromDrag(
        camera: THREE.Camera,
        rectd: Rectd,
        candidates: OrthoFrameCandidate[]
    ): RepairOrthoFrame | null {
        if (!candidates || candidates.length === 0) return null;

        const isCenterShape = rectd.shapeType === 'circle' || rectd.shapeType === 'ellipse';
        const centerX = isCenterShape ? rectd.centerX : (rectd.inix + rectd.endx) / 2;
        const centerY = isCenterShape ? rectd.centerY : (rectd.iniy + rectd.endy) / 2;
        if (centerX === undefined || centerY === undefined) return null;

        let halfExtentX: number;
        let halfExtentY: number;
        if (rectd.shapeType === 'circle') {
            halfExtentX = halfExtentY = rectd.radius ?? 0;
        } else if (rectd.shapeType === 'ellipse') {
            halfExtentX = rectd.radiusX ?? 0;
            halfExtentY = rectd.radiusY ?? 0;
        } else {
            halfExtentX = Math.abs(rectd.endx - rectd.inix) / 2;
            halfExtentY = Math.abs(rectd.endy - rectd.iniy) / 2;
        }

        const meshes = candidates.map((c) => c.mesh);
        const raycaster = new THREE.Raycaster();
        const castScreenRay = (sx: number, sy: number): THREE.Raycaster => {
            const ndcX = (sx / window.innerWidth) * 2 - 1;
            const ndcY = (-sy / window.innerHeight) * 2 + 1;
            raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
            return raycaster;
        };
        const tryHit = (sx: number, sy: number): THREE.Intersection | null => {
            const hits = castScreenRay(sx, sy).intersectObjects(meshes, false);
            return hits.length ? hits[0] : null;
        };

        // Probe the anchor (the user's first click — the strongest intent signal) before the
        // centre: when a drag starts on an element and sweeps past its edge, the centre ray can
        // miss it or hit unrelated structure behind, putting the frame on the wrong surface.
        // Mid-edge probes and, for rectangles, the drawn corners give further fallbacks so the
        // frame survives drags that only partially overlap the element.
        const probePoints: [number, number][] = [];
        if (!isCenterShape && rectd.origx !== undefined && rectd.origy !== undefined) {
            probePoints.push([rectd.origx, rectd.origy]);
        }
        probePoints.push(
            [centerX, centerY],
            [centerX - halfExtentX / 2, centerY],
            [centerX + halfExtentX / 2, centerY],
            [centerX, centerY - halfExtentY / 2],
            [centerX, centerY + halfExtentY / 2],
        );
        if (!isCenterShape) {
            probePoints.push(
                [rectd.inix, rectd.iniy],
                [rectd.inix, rectd.endy],
                [rectd.endx, rectd.endy],
                [rectd.endx, rectd.iniy],
            );
        }
        let hit: THREE.Intersection | null = null;
        for (const [px, py] of probePoints) {
            hit = tryHit(px, py);
            if (hit) break;
        }
        if (!hit) return null;

        const candidate = candidates.find((c) => c.mesh === hit.object);
        if (!candidate) return null;

        const rawNormal = candidate.element.elementNormal
            ? candidate.element.elementNormal
            : candidate.element.section?.sectionNormal;
        if (!rawNormal) return null;

        const frame = new RepairOrthoFrame();
        frame.shapeType = rectd.shapeType;
        frame.hitElement = candidate.element;
        frame.n = new THREE.Vector3(rawNormal.x, rawNormal.y, rawNormal.z);
        if (frame.n.lengthSq() < 1e-12) return null;
        frame.n.normalize();
        // orient the projection direction toward the camera
        if (frame.n.dot(camera.position.clone().sub(hit.point)) < 0) frame.n.negate();

        // Capture the real surface plane (true normal, pre-snap) before the canonical snap
        // below may rotate `n`. The inserted area lies on this plane.
        frame.hitPoint = hit.point.clone();
        frame.surfaceNormal = frame.n.clone();

        // Snap the projection to the canonical world axis closest to the SECTION normal —
        // stable regardless of the camera position (a side shell section with normal Y
        // always snaps to ±Y, however inclined it appears on screen). A snap is only
        // accepted while the hit element's own surface is still reasonably front-on to
        // that axis; otherwise keep the exact element normal to avoid re-introducing
        // distortion on inclined/curved elements.
        // Stiffener webs / brackets / faceplates stand on the section plating, so their
        // normal is ~perpendicular to the section normal and the section-based snap can
        // never pass the tolerance check — for them, fall back to the canonical axis
        // closest to the element's own normal (equally camera-independent).
        const sectionNormal = candidate.element.section?.sectionNormal;
        const snapSources: THREE.Vector3[] = [];
        if (sectionNormal) {
            snapSources.push(new THREE.Vector3(sectionNormal.x, sectionNormal.y, sectionNormal.z));
        }
        snapSources.push(frame.n.clone());
        for (const snapSource of snapSources) {
            if (snapSource.lengthSq() <= 1e-12) continue;
            const snappedAxis = RepairOrthoFrame.closestCanonicalAxis(snapSource.normalize());
            // orient toward the camera, like the projection direction
            if (snappedAxis.dot(camera.position.clone().sub(hit.point)) < 0) snappedAxis.negate();
            if (snappedAxis.dot(frame.n) >= RepairOrthoFrame.SNAP_TOLERANCE_COS) {
                frame.n.copy(snappedAxis);
                frame.canonicalBasis = true;
                break;
            }
        }

        // Project the drawn outline onto the TRUE element surface plane (surfaceNormal), not an
        // n-normal plane: with the canonical snap, `n` differs from the surface normal, so an
        // n-normal plane through the moving center hit slides along `n` as the mouse drags —
        // making the anchor corner drift. The true plate plane is constant for a flat element.
        const surfacePlane = new THREE.Plane().setFromNormalAndCoplanarPoint(frame.surfaceNormal, hit.point);
        const projectPixel = (sx: number, sy: number): THREE.Vector3 | null => {
            const ray = castScreenRay(sx, sy).ray;
            if (Math.abs(ray.direction.dot(frame.n)) >= RepairOrthoFrame.GRAZING_LIMIT) {
                const out = new THREE.Vector3();
                if (ray.intersectPlane(surfacePlane, out)) return out;
            }
            // Horizon fallback: a grazing ray (or one meeting the surface plane behind the
            // camera) would previously null this pixel and drop the WHOLE drag to the legacy
            // algorithm. Instead tilt the ray minimally toward the plane — pin its component
            // along the surface normal at the grazing limit — and intersect the surface plane
            // with that. The point lands far along the plane (where a near-horizon pixel
            // genuinely points), always in front of the camera, with finite extents.
            const clamped = ray.direction
                .clone()
                .addScaledVector(
                    frame.surfaceNormal,
                    -RepairOrthoFrame.GRAZING_LIMIT - ray.direction.dot(frame.surfaceNormal))
                .normalize();
            const clampedRay = new THREE.Ray(ray.origin.clone(), clamped);
            const out = new THREE.Vector3();
            return clampedRay.intersectPlane(surfacePlane, out) ? out : null;
        };

        let ok = false;
        if (rectd.shapeType === 'circle') {
            ok = frame.initCircle(camera, rectd, projectPixel);
        } else if (rectd.shapeType === 'ellipse') {
            ok = frame.initEllipse(camera, rectd, projectPixel);
        } else {
            ok = frame.initRectangle(rectd, projectPixel);
        }
        return ok ? frame : null;
    }

    private initRectangle(rectd: Rectd, projectPixel: (sx: number, sy: number) => THREE.Vector3 | null): boolean {
        // same screen-space corner order as rectd.geom: TL, BL, BR, TR
        const pixels: [number, number][] = [
            [rectd.inix, rectd.iniy],
            [rectd.inix, rectd.endy],
            [rectd.endx, rectd.endy],
            [rectd.endx, rectd.iniy],
        ];
        const f = pixels.map((p) => projectPixel(p[0], p[1]));
        if (f.some((p) => p == null)) return false;

        // basis from the averaged top + bottom projected edge directions, forced in-plane
        const eU = f[3].clone().sub(f[0]).add(f[2].clone().sub(f[1]));
        if (this.canonicalBasis) {
            this.setCanonicalBasis(eU);
        } else {
            let u = eU.clone().sub(this.n.clone().multiplyScalar(eU.dot(this.n)));
            if (u.lengthSq() < 1e-12) {
                u = this.fallbackInPlaneAxis();
                if (!u) return false;
            }
            this.u = u.normalize();
            this.v = new THREE.Vector3().crossVectors(this.n, this.u).normalize();
        }

        // Anchor at the corner the user first clicked (rectd.origx/y) and treat the opposite
        // (mouse) corner as the dragged corner; build the world-aligned rectangle that spans the
        // two diagonal corners along u and v. Measuring the extents from these two corners — rather
        // than from the screen-horizontal/vertical edges — makes the area grow along the world axis
        // the mouse actually moves toward. The old edge-based formula tied width to the screen-X
        // edge and height to the screen-Y edge, so when u/v were rotated on screen, dragging one
        // way grew the perpendicular direction instead.
        const anchorLeft =
            Math.abs(rectd.origx - rectd.inix) <= Math.abs(rectd.origx - rectd.endx);
        const anchorTop =
            Math.abs(rectd.origy - rectd.iniy) <= Math.abs(rectd.origy - rectd.endy);
        // pixels order: 0 = TL(ini,ini), 1 = BL(ini,end), 2 = BR(end,end), 3 = TR(end,ini)
        const anchorIndex = anchorLeft ? (anchorTop ? 0 : 1) : (anchorTop ? 3 : 2);

        const anchor = f[anchorIndex].clone();
        const opposite = f[(anchorIndex + 2) % 4];
        const delta = opposite.clone().sub(anchor);
        // signed spans from the anchor toward the mouse along each world axis
        const du = this.u.clone().multiplyScalar(delta.dot(this.u));
        const dv = this.v.clone().multiplyScalar(delta.dot(this.v));

        this.halfWidth = du.length() / 2;
        this.halfHeight = dv.length() / 2;
        if (this.halfWidth < RepairOrthoFrame.MIN_EXTENT || this.halfHeight < RepairOrthoFrame.MIN_EXTENT) {
            return false;
        }

        this.origin = anchor.clone().add(du.clone().multiplyScalar(0.5)).add(dv.clone().multiplyScalar(0.5));
        this.rectCorners = [
            anchor.clone(),
            anchor.clone().add(du),
            anchor.clone().add(du).add(dv),
            anchor.clone().add(dv),
        ];
        return true;
    }

    private initCircle(
        camera: THREE.Camera,
        rectd: Rectd,
        projectPixel: (sx: number, sy: number) => THREE.Vector3 | null
    ): boolean {
        const origin = projectPixel(rectd.centerX, rectd.centerY);
        if (!origin || !rectd.radius) return false;
        this.origin = origin;

        // mean of 8 projected outline samples (same sampling as RepairGeometryClipper.initFromCircle)
        let sum = 0;
        let count = 0;
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            const p = projectPixel(
                rectd.centerX + rectd.radius * Math.cos(angle),
                rectd.centerY + rectd.radius * Math.sin(angle)
            );
            if (!p) return false;
            sum += p.distanceTo(this.origin);
            count++;
        }
        this.radius = sum / count;
        if (this.radius < RepairOrthoFrame.MIN_EXTENT) return false;

        if (this.canonicalBasis) {
            this.setCanonicalBasis(null);
        } else if (!this.initBasisFromCameraUp(camera)) {
            return false;
        }
        this.rectCorners = this.bboxCorners(this.radius, this.radius);
        return true;
    }

    private initEllipse(
        camera: THREE.Camera,
        rectd: Rectd,
        projectPixel: (sx: number, sy: number) => THREE.Vector3 | null
    ): boolean {
        const origin = projectPixel(rectd.centerX, rectd.centerY);
        if (!origin || !rectd.radiusX || !rectd.radiusY) return false;
        this.origin = origin;

        const pXPos = projectPixel(rectd.centerX + rectd.radiusX, rectd.centerY);
        const pXNeg = projectPixel(rectd.centerX - rectd.radiusX, rectd.centerY);
        const pYPos = projectPixel(rectd.centerX, rectd.centerY + rectd.radiusY);
        const pYNeg = projectPixel(rectd.centerX, rectd.centerY - rectd.radiusY);
        if (!pXPos || !pXNeg || !pYPos || !pYNeg) return false;

        // u = projected screen-X direction at the shape, forced in-plane
        const eU = pXPos.clone().sub(pXNeg);
        if (this.canonicalBasis) {
            this.setCanonicalBasis(eU);
        } else {
            let u = eU.clone().sub(this.n.clone().multiplyScalar(eU.dot(this.n)));
            if (u.lengthSq() < 1e-12) {
                if (!this.initBasisFromCameraUp(camera)) return false;
            } else {
                this.u = u.normalize();
                this.v = new THREE.Vector3().crossVectors(this.n, this.u).normalize();
            }
        }

        this.radiusU = (pXPos.distanceTo(this.origin) + pXNeg.distanceTo(this.origin)) / 2;
        this.radiusV = (pYPos.distanceTo(this.origin) + pYNeg.distanceTo(this.origin)) / 2;
        if (this.radiusU < RepairOrthoFrame.MIN_EXTENT || this.radiusV < RepairOrthoFrame.MIN_EXTENT) {
            return false;
        }

        this.rectCorners = this.bboxCorners(this.radiusU, this.radiusV);
        return true;
    }

    /** Canonical world axis (±X/±Y/±Z) closest to the given direction. */
    private static closestCanonicalAxis(dir: THREE.Vector3): THREE.Vector3 {
        const ax = Math.abs(dir.x);
        const ay = Math.abs(dir.y);
        const az = Math.abs(dir.z);
        if (ax >= ay && ax >= az) return new THREE.Vector3(Math.sign(dir.x) || 1, 0, 0);
        if (ay >= ax && ay >= az) return new THREE.Vector3(0, Math.sign(dir.y) || 1, 0);
        return new THREE.Vector3(0, 0, Math.sign(dir.z) || 1);
    }

    /** The two canonical axes perpendicular to the (canonical) projection direction. */
    private inPlaneCanonicalAxes(): THREE.Vector3[] {
        const axes = [
            new THREE.Vector3(1, 0, 0),
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, 1),
        ];
        return axes.filter((a) => Math.abs(a.dot(this.n)) < 0.5);
    }

    /**
     * World-aligned basis for the snapped projection: u is the in-plane canonical axis
     * closest to `preferredU` (so the drawn width still maps to the drawn direction),
     * v completes the right-handed frame.
     */
    private setCanonicalBasis(preferredU: THREE.Vector3 | null): void {
        const [a1, a2] = this.inPlaneCanonicalAxes();
        if (preferredU) {
            const d1 = preferredU.dot(a1);
            const d2 = preferredU.dot(a2);
            this.u = Math.abs(d1) >= Math.abs(d2)
                ? a1.multiplyScalar(Math.sign(d1) || 1)
                : a2.multiplyScalar(Math.sign(d2) || 1);
        } else {
            this.u = a1;
        }
        this.v = new THREE.Vector3().crossVectors(this.n, this.u).normalize();
    }

    private initBasisFromCameraUp(camera: THREE.Camera): boolean {
        const camUp = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
        let u = new THREE.Vector3().crossVectors(camUp, this.n);
        if (u.lengthSq() < 1e-12) {
            u = this.fallbackInPlaneAxis();
            if (!u) return false;
        }
        this.u = u.normalize();
        this.v = new THREE.Vector3().crossVectors(this.n, this.u).normalize();
        return true;
    }

    private fallbackInPlaneAxis(): THREE.Vector3 | null {
        const arbitrary = Math.abs(this.n.y) > 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
        const u = new THREE.Vector3().crossVectors(arbitrary, this.n);
        return u.lengthSq() < 1e-12 ? null : u;
    }

    private bboxCorners(hw: number, hh: number): THREE.Vector3[] {
        return [
            this.uvPoint(-hw, hh),
            this.uvPoint(hw, hh),
            this.uvPoint(hw, -hh),
            this.uvPoint(-hw, -hh),
        ];
    }

    public uvPoint(uu: number, vv: number): THREE.Vector3 {
        return this.origin
            .clone()
            .add(this.u.clone().multiplyScalar(uu))
            .add(this.v.clone().multiplyScalar(vv));
    }

    /** In-plane coordinates of a world point; projection along `n` is implicit (u, v ⊥ n). */
    public toUV(pWorld: THREE.Vector3): { u: number; v: number } {
        const q = pWorld.clone().sub(this.origin);
        return { u: q.dot(this.u), v: q.dot(this.v) };
    }

    /** Prism (rectangle) / infinite cylinder (circle) / elliptic cylinder (ellipse) test. */
    public isInside(pWorld: THREE.Vector3): boolean {
        const { u, v } = this.toUV(pWorld);
        const tol = RepairOrthoFrame.INSIDE_TOLERANCE;
        if (this.shapeType === 'circle') {
            return u * u + v * v <= this.radius * this.radius + tol;
        }
        if (this.shapeType === 'ellipse') {
            const nu = u / this.radiusU;
            const nv = v / this.radiusV;
            return nu * nu + nv * nv <= 1.0 + tol;
        }
        return Math.abs(u) <= this.halfWidth + tol && Math.abs(v) <= this.halfHeight + tol;
    }

    /**
     * Rectified shape corners (rectangle), or the rectified bounding-box corners
     * (circle/ellipse — same role the screen-space bbox plays in the legacy clipper).
     */
    public getRectCorners(): THREE.Vector3[] {
        return this.rectCorners.map((c) => c.clone());
    }

    /**
     * Closed world-space outline of the rectified footprint — the boundary of the area
     * that will actually be inserted — for drawing a live preview that matches the drop.
     * Rectangle returns its four rectified corners; circle/ellipse returns `segments`
     * sampled boundary points. Each point is projected along `n` onto the TRUE element
     * surface plane (the same plane the inserted geometry lands on), so a canonical-axis
     * snap or a tilted/curved element does not make the preview diverge from the result.
     */
    public getPreviewOutline(segments = 64): THREE.Vector3[] {
        const pts =
            this.shapeType === 'rectangle' ? this.getRectCorners() : this.sampleOutlineWorld(segments);
        return pts.map((p) => this.projectOntoSurfacePlane(p));
    }

    /**
     * Projects a point along the projection direction `n` onto the true surface plane
     * (through `hitPoint` with `surfaceNormal`). This reproduces how the inserted area's
     * corners are derived (prism side-planes ∩ surface plane). Returns the point unchanged
     * when the surface is too edge-on to `n` for a stable projection, or before the surface
     * plane has been captured.
     */
    public projectOntoSurfacePlane(p: THREE.Vector3): THREE.Vector3 {
        if (!this.surfaceNormal || !this.hitPoint) return p.clone();
        const denom = this.surfaceNormal.dot(this.n);
        if (Math.abs(denom) < 1e-6) return p.clone();
        const t = this.surfaceNormal.dot(this.hitPoint.clone().sub(p)) / denom;
        return p.clone().add(this.n.clone().multiplyScalar(t));
    }

    /** A (u, v) point of the frame, projected onto the true surface plane (for on-surface rulers). */
    public surfaceUVPoint(uu: number, vv: number): THREE.Vector3 {
        return this.projectOntoSurfacePlane(this.uvPoint(uu, vv));
    }

    /**
     * Prism side planes, sign-normalized so the inside is positive
     * (required by the `closestDistance >= -0.001` checks in GraphicsRenderRepair).
     */
    public getSidePlanes(): THREE.Plane[] {
        return RepairOrthoFrame.prismPlanes(this.rectCorners, this.n, this.origin);
    }

    public static prismPlanes(corners: THREE.Vector3[], n: THREE.Vector3, insideRef: THREE.Vector3): THREE.Plane[] {
        const planes: THREE.Plane[] = [];
        for (let i = 0; i < 4; i++) {
            const a = corners[i];
            const b = corners[(i + 1) % 4];
            const pl = new THREE.Plane().setFromCoplanarPoints(b.clone().add(n), b, a);
            if (pl.distanceToPoint(insideRef) < 0) pl.negate();
            planes.push(pl);
        }
        return planes;
    }

    public segmentCircleIntersections(startWorld: THREE.Vector3, endWorld: THREE.Vector3): THREE.Vector3[] {
        const s = this.toUV(startWorld);
        const e = this.toUV(endWorld);
        const dx = e.u - s.u;
        const dy = e.v - s.v;
        return this.solveSegmentQuadratic(
            startWorld, endWorld,
            dx * dx + dy * dy,
            2 * (s.u * dx + s.v * dy),
            s.u * s.u + s.v * s.v - this.radius * this.radius
        );
    }

    public segmentEllipseIntersections(startWorld: THREE.Vector3, endWorld: THREE.Vector3): THREE.Vector3[] {
        // scale uv to the unit circle, then reuse the circle quadratic
        const s = this.toUV(startWorld);
        const e = this.toUV(endWorld);
        const su = s.u / this.radiusU;
        const sv = s.v / this.radiusV;
        const dx = e.u / this.radiusU - su;
        const dy = e.v / this.radiusV - sv;
        return this.solveSegmentQuadratic(
            startWorld, endWorld,
            dx * dx + dy * dy,
            2 * (su * dx + sv * dy),
            su * su + sv * sv - 1
        );
    }

    /**
     * Solves a*t^2 + b*t + c = 0 for t in the uv-projected segment and lerps the WORLD
     * endpoints with the same t — exact, because projection along `n` is affine in t
     * (no perspective correction needed, unlike the screen-space variants).
     * Same t-tolerance and duplicate filtering as findLineCircleIntersectionsScreenSpace.
     */
    private solveSegmentQuadratic(
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        a: number,
        b: number,
        c: number
    ): THREE.Vector3[] {
        const intersections: THREE.Vector3[] = [];
        if (a < 1e-18) return intersections;

        const discriminant = b * b - 4 * a * c;
        if (discriminant < 0) return intersections;

        const sqrtDiscriminant = Math.sqrt(discriminant);
        const t1 = (-b - sqrtDiscriminant) / (2 * a);
        const t2 = (-b + sqrtDiscriminant) / (2 * a);

        const tolerance = 0.001;
        if (t1 >= -tolerance && t1 <= 1 + tolerance) {
            intersections.push(startWorld.clone().lerp(endWorld, Math.max(0, Math.min(1, t1))));
        }
        if (Math.abs(t1 - t2) > tolerance && t2 >= -tolerance && t2 <= 1 + tolerance) {
            const p = startWorld.clone().lerp(endWorld, Math.max(0, Math.min(1, t2)));
            if (intersections.length === 0 || intersections.every((int) => int.distanceTo(p) > 0.001)) {
                intersections.push(p);
            }
        }
        return intersections;
    }

    /** World points on the rectified circle/ellipse boundary (for boundary sampling). */
    public sampleOutlineWorld(numSamples: number): THREE.Vector3[] {
        const rU = this.shapeType === 'circle' ? this.radius : this.radiusU;
        const rV = this.shapeType === 'circle' ? this.radius : this.radiusV;
        const points: THREE.Vector3[] = [];
        for (let i = 0; i < numSamples; i++) {
            const angle = (i / numSamples) * Math.PI * 2;
            points.push(this.uvPoint(rU * Math.cos(angle), rV * Math.sin(angle)));
        }
        return points;
    }

    /**
     * Four overlapping complement rectangles around the rectified shape in the (u, v) plane
     * (top / left / bottom / right), used by cut mode to clip the original plate minus the hole.
     * `extent` must cover the whole target plate in uv units.
     */
    public buildComplementRectCorners(extent: number): THREE.Vector3[][] {
        let hw: number;
        let hh: number;
        if (this.shapeType === 'circle') {
            hw = hh = this.radius;
        } else if (this.shapeType === 'ellipse') {
            hw = this.radiusU;
            hh = this.radiusV;
        } else {
            hw = this.halfWidth;
            hh = this.halfHeight;
        }
        const B = extent;
        return [
            // top
            [this.uvPoint(-B, hh), this.uvPoint(B, hh), this.uvPoint(B, B), this.uvPoint(-B, B)],
            // left
            [this.uvPoint(-B, -B), this.uvPoint(-hw, -B), this.uvPoint(-hw, B), this.uvPoint(-B, B)],
            // bottom
            [this.uvPoint(-B, -B), this.uvPoint(B, -B), this.uvPoint(B, -hh), this.uvPoint(-B, -hh)],
            // right
            [this.uvPoint(hw, -B), this.uvPoint(B, -B), this.uvPoint(B, B), this.uvPoint(hw, B)],
        ];
    }
}
